// Quotation items page (route APP_ROUTES.QUOTATION.QUOTATION_LIST = "/quotations", "View Quotations" pill).
// Shows every product template (description + size/price variants) as a card.
// The edit dialog only changes local state and logs it (no API call yet); the delete button has no handler.
import Tabs from "./tabs";
import {
  Avatar,
  Box,
  Card,
  Grid,
  IconButton,
  Stack,
  Typography,
  Tooltip,
  Dialog,
  DialogContent,
  Button, TextField
} from "@mui/material";
import { useSelector } from "react-redux";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import StraightenIcon from "@mui/icons-material/Straighten";
import { useState } from "react";
import { QR, QrModalHeader, qrDialogPaperSx } from "../../qrCode/components/qrUi";
import { backBtnSx, nextBtnSx } from "../../campaign/components/stepUi";

// Rounded input look (blue focus) for the plain TextFields in the edit dialog
const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2.5,
    bgcolor: "#fff",
    "& .MuiOutlinedInput-notchedOutline": { borderWidth: "1.5px", borderColor: "#e5e7eb" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderWidth: "1.5px", borderColor: QR.blue },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: QR.blue },
};

// Small rounded icon button style for the card actions (color passed in).
const iconBtnSx = (c) => ({
  width: 32,
  height: 32,
  borderRadius: 2,
  color: c,
  bgcolor: `${c}10`,
  border: `1px solid ${c}26`,
  "&:hover": { bgcolor: c, color: "#fff" },
});

// Reads templates from state.crmQuotation (loaded by Tabs on mount).
const CrmViewQuotation = () => {
  const { quotationTemplates = [] } = useSelector(
    (state) => state.crmQuotation
  );

  // Edit dialog state; editData is a copy of the item being edited. selectedItem appears unused.
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editData, setEditData] = useState({
    item_desc: "",
    variables: [],
  });

  // Closes the edit dialog.
  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedItem(null);
  };

  // Opens the edit dialog with a copy of the item's description and variants (amount, size, gst).
  const handleEditOpen = (item) => {
    setEditData({
      item_desc: item.item_desc,
      variables: item.variables.map(v => ({
        amount: v.amount,
        size: v.size,
        gst: v.gst || ""
      }))
    });
    setOpenDialog(true);
  };
  // Updates the description in the edit copy.
  const handleDescriptionChange = (e) => {
    setEditData((prev) => ({
      ...prev,
      item_desc: e.target.value,
    }));
  };

  // Save: currently only logs the edited data to the console and closes; nothing is persisted.
  const handleSave = () => {
    console.log("Updated Data:", editData);
    handleDialogClose();
  };

  // Updates one field (amount/size/gst) of one variant in the edit copy.
  const handleVariableChange = (index, field, value) => {
    setEditData(prev => {
      const updatedVariables = [...prev.variables];
      updatedVariables[index] = {
        ...updatedVariables[index],
        [field]: value,
      };

      return {
        ...prev,
        variables: updatedVariables,
      };
    });
  };

  // Summary cards (display only)
  const stats = [
    { label: "Products", value: quotationTemplates.length, icon: <Inventory2OutlinedIcon />, color: QR.main },
    { label: "Sizes / Variants", value: quotationTemplates.reduce((n, t) => n + (t.variables?.length || 0), 0), icon: <StraightenIcon />, color: QR.purple },
  ];

  return (
    <div>
      <Tabs stats={stats}>
        <Box sx={{ pt: 2 }}>
          {quotationTemplates.length === 0 ? (
            <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
              <Inventory2OutlinedIcon sx={{ fontSize: 40, color: "#cbd5e1" }} />
              <Typography variant="body2" sx={{ mt: 1 }}>No quotation items found.</Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {/* One card per product: title = text before the first "•" in item_desc, chips = size | price variants. */}
              {quotationTemplates.map((item, index) => (
                <Grid item size={{ xs: 12, md: 6 }} key={index}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: 3,
                      border: `1px solid ${QR.border}`,
                      boxShadow: "0 1px 3px rgba(16,24,40,0.06)",
                      transition: "all .15s ease",
                      "&:hover": {
                        borderColor: `${QR.blue}55`,
                        boxShadow: "0 8px 20px -12px rgba(15,23,42,0.35)",
                      },
                    }}
                  >
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5} sx={{ p: { xs: 1.5, sm: 2 }, flex: 1 }}>
                      <Stack direction="row" alignItems="flex-start" spacing={1.25} sx={{ minWidth: 0 }}>
                        <Avatar variant="rounded" sx={{ width: 34, height: 34, borderRadius: 2, bgcolor: `${QR.main}14`, color: QR.main }}>
                          <Inventory2OutlinedIcon sx={{ fontSize: 19 }} />
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={700} color="#0f172a" sx={{ wordBreak: "break-word" }}>
                            {item.item_desc.split("•")[0]}
                          </Typography>

                          <Stack direction="row" sx={{ mt: 1, flexWrap: "wrap", gap: 0.75 }}>
                            {item.variables.map((itemInfo, i) => (
                              <Box
                                key={i}
                                component="span"
                                sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, px: 1, py: 0.3, borderRadius: 5, fontSize: 12, color: "#334155", bgcolor: "#f1f5f9", border: "1px solid #e2e8f0" }}
                              >
                                <b>Size</b> {itemInfo.size}
                                <Box component="span" sx={{ color: "#cbd5e1" }}>|</Box>
                                <Box component="span" sx={{ fontWeight: 700, color: QR.main }}>₹ {itemInfo.amount}</Box>
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      </Stack>

                      <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0 }}>
                        <Tooltip title={`Edit ${item.item_desc.substring(0, 50)}`}>
                          <IconButton size="small" onClick={() => handleEditOpen(item)} sx={iconBtnSx(QR.blue)}>
                            <EditIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>

                        {/* Delete has no onClick handler yet. */}
                        <Tooltip title={`Delete ${item.item_desc.substring(0, 50)}`}>
                          <IconButton size="small" sx={iconBtnSx("#dc2626")}>
                            <DeleteIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Tabs>

      {/* 🔹 EDIT DIALOG */}
      <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="sm" fullWidth PaperProps={{ sx: qrDialogPaperSx }}>
        <QrModalHeader icon={<EditIcon />} title="Edit Quotation" subtitle="Update the item description, amount, size and GST" onClose={handleDialogClose} />

        <DialogContent sx={{ p: { xs: 2, sm: 2.5 } }}>
          {/* Description */}
          <TextField
            label="Item Description"
            multiline
            minRows={2}
            fullWidth
            // Shows only the text before the first "•"; typing replaces item_desc with that text, dropping the bullet details.
            value={editData.item_desc.split("•")[0]}
            onChange={handleDescriptionChange}
            sx={{ mb: 2, mt: 1, ...inputSx }}
          />

          {/* <Box mb={3}>
            {quotationTemplates.map((item, index) => (
              <Typography fontWeight="bold" key={index}>
                {index === 0 &&
                  item.item_desc.split("•").map((text, i) =>
                    i !== 0 ? (
                      <span key={i}>
                        <br />•{text}
                      </span>
                    ) : null
                  )}
              </Typography>
            ))}
          </Box> */}

          {/* Amount / Size / GST */}
          {editData.variables.map((v, i) => (
            <Box
              key={i}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
                gap: 1.5,
                mb: 1.5,
                p: 1.5,
                borderRadius: 2.5,
                bgcolor: "#f8fafc",
                border: `1px solid ${QR.border}`,
              }}
            >
              <TextField
                size="small"
                label="Amount"
                type="number"
                fullWidth
                value={v.amount}
                onChange={(e) =>
                  handleVariableChange(i, "amount", e.target.value)
                }
                sx={inputSx}
              />

              <TextField
                size="small"
                label="Size"
                fullWidth
                value={v.size}
                onChange={(e) =>
                  handleVariableChange(i, "size", e.target.value)
                }
                sx={inputSx}
              />

              <TextField
                size="small"
                label="GST (%)"
                type="number"
                fullWidth
                value={v.gst}
                onChange={(e) =>
                  handleVariableChange(i, "gst", e.target.value)
                }
                sx={inputSx}
              />
            </Box>
          ))}
        </DialogContent>

        <Stack
          direction={{ xs: "column-reverse", sm: "row" }}
          spacing={1.25}
          justifyContent="flex-end"
          sx={{ px: { xs: 2, sm: 2.5 }, py: 1.5, borderTop: `1px solid ${QR.border}`, "& .MuiButton-root": { width: { xs: "100%", sm: "auto" } } }}
        >
          <Button onClick={handleDialogClose} sx={backBtnSx}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} sx={nextBtnSx}>
            Save
          </Button>
        </Stack>
      </Dialog>
    </div>
  );
};

export default CrmViewQuotation;
